// src/components/Plus.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, NavLink, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { API_URL, SOCKET_URL } from '../config';
import { apiFetch, getAccessToken, logout } from '../utils/auth';
import { getRangiDeviceId } from '../utils/deviceId';
import { formatTaskStatusChangedText, stripEndOfDayDeadlineTime } from '../utils/taskNotifications';
import { useEmployeeId } from '../context/RoleContext';
import { useRoleState } from '../context/RoleContext';
import s from './Plus.module.css';

/*  SVG-иконки из src/icons */
import { ReactComponent as AppLogo } from '../icons/app_black_logo.svg';
import { ReactComponent as SidebarLogo } from '../icons/app_white_logo.svg';
import { ReactComponent as HideLine } from '../icons/hide_notifications.svg'; // линия-переключатель
import { ReactComponent as AdminBtnIcon } from '../icons/admin_btn.svg';
import { ReactComponent as AppinfoBtnIcon } from '../icons/about_app.svg';
import newMessageIcon from '../icons/new_message.svg';
import setImportanceIcon from '../icons/set_importance.svg';
import importantNewsIcon from '../icons/important_news.svg';
import newNewsIcon from '../icons/new_news.svg'
import birthdayNewsIcon from '../icons/birthday_news.svg';
import unreadGroupsIcon from '../icons/unread_groups.svg';
import { ReactComponent as SortNotificationsIcon } from '../icons/sort_notifications.svg';
import { ReactComponent as DoneIcon } from '../icons/done.svg';
import { ReactComponent as InfoSquareIcon } from '../icons/info_square.svg';
import { ReactComponent as ClickboardCheckIcon } from '../icons/clickboard_check.svg';
import { ReactComponent as FileShieldIcon } from '../icons/file_shield.svg';
import { ReactComponent as TrophyIcon } from '../icons/trophy.svg';
import { ReactComponent as TasksIcon } from '../icons/tasks_icon.svg';
import { SearchInput as SearchInputComponent, Textarea, EmployeeTaskStatsAvatar, Avatar } from './UI';
import { useEmployeeTaskStats } from '../utils/employeeTaskStats';
import { ReactComponent as DexterIcon } from '../icons/dexter_icon.svg';

// базовый origin для /uploads/*
const API_ORIGIN = (() => {
  try { return API_URL.replace(/\/api\/v\d+\/?$/i, ''); } catch { return ''; }
})();
export const resolveAvatar = (url) => (!url ? null : (url.startsWith('/uploads/') ? `${API_ORIGIN}${url}` : url));

// Глобальные кэши (живут между открытиями модалки)
const EMP_AVATAR_CACHE = new Map(); // id -> url
const TEAM_IMAGE_CACHE = new Map(); // id -> url

// данные о текущем сотруднике (для карточки профиля в сайдбаре)
export const useMe = (employeeId) => {
  const [me, setMe] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!employeeId) return;
    apiFetch(`${API_URL}/employees/${employeeId}`).then(async (r) => {
      if (!alive) return;
      const j = r.ok ? await r.json() : null;
      if (j) setMe(j);
    }).catch(() => { });
    return () => { alive = false; };
  }, [employeeId]);
  return me;
};

// простой хук-дебаунс без лишних зависимостей
function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  const t = useRef(null);
  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setV(value), delay);
    return () => t.current && clearTimeout(t.current);
  }, [value, delay]);
  return v;
}


function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();

    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    if (d.toDateString() === y.toDateString()) {
      return 'Вчера';
    }

    return d.toLocaleDateString('ru-RU');
  } catch {
    return '';
  }
}

function truncateText(value, maxLength) {
  const text = (value || '').trim();
  if (!text) return '';
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

const TASK_STATUS_LABELS = {
  new: 'Новая',
  in_progress: 'В работе',
  done: 'Выполнено',
  accepted: 'Принята',
  archive: 'Архив',
};

function isDateOnlyDeadline(date) {
  const h = date.getHours();
  const m = date.getMinutes();
  return (h === 23 && m === 59) || (h === 0 && m === 0);
}

function formatTaskDeadline(iso) {
  if (!iso) return 'Без срока';
  try {
    const d = new Date(iso);
    const now = new Date();
    const isSameYear = d.getFullYear() === now.getFullYear();
    const dateOnly = isDateOnlyDeadline(d);
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      ...(isSameYear ? {} : { year: 'numeric' }),
      ...(dateOnly ? {} : { hour: '2-digit', minute: '2-digit' }),
    });
  } catch {
    return 'Без срока';
  }
}

function isActiveDeadlineTask(task) {
  const status = String(task?.status || '');
  return status === 'new' || status === 'in_progress';
}

function getTaskNotifActorName(notif) {
  const name = (notif?.actor?.name || '').trim();
  return name || 'Сотрудник';
}

function quotedTaskTitle(notif, fallback = '') {
  const title = (notif?.task_title || fallback || '').trim();
  return title ? ` «${title}»` : '';
}

function groupTaskNotificationsByTask(notifications) {
  if (!Array.isArray(notifications) || notifications.length === 0) return [];

  const byTask = new Map();

  for (const n of notifications) {
    const taskId = Number(n?.task_id);
    if (!Number.isFinite(taskId) || taskId <= 0) continue;

    const notifId = Number(n?.id);
    const cur = byTask.get(taskId);

    if (!cur) {
      byTask.set(taskId, {
        latest: n,
        task_id: taskId,
        task_title: n.task_title,
        created_at: n.created_at,
        unread_count: 1,
        notification_ids: Number.isFinite(notifId) ? [notifId] : [],
      });
      continue;
    }

    cur.unread_count += 1;
    if (Number.isFinite(notifId)) cur.notification_ids.push(notifId);

    const tNew = new Date(n.created_at || 0).getTime() || 0;
    const tOld = new Date(cur.latest?.created_at || 0).getTime() || 0;
    if (tNew >= tOld) {
      cur.latest = n;
      cur.created_at = n.created_at;
      if (n.task_title) cur.task_title = n.task_title;
    }
  }

  return Array.from(byTask.values())
    .map((g) => ({
      ...g.latest,
      task_id: g.task_id,
      task_title: g.latest?.task_title || g.task_title,
      created_at: g.latest?.created_at || g.created_at,
      unread_count: g.unread_count,
      notification_ids: g.notification_ids,
    }))
    .sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime() || 0;
      const tb = new Date(b.created_at || 0).getTime() || 0;
      return tb - ta;
    });
}

function formatTaskNotificationEvent(notif) {
  const type = String(notif?.type || '').trim();
  const body = stripEndOfDayDeadlineTime(String(notif?.body || '').trim());
  const title = stripEndOfDayDeadlineTime(String(notif?.title || '').trim());
  const actor = getTaskNotifActorName(notif);
  const taskQ = quotedTaskTitle(notif);

  if (type === 'task_status_changed') {
    if (title) return title;
    if (body) return body;
    return `${actor} изменил(а) статус задачи${taskQ}`;
  }

  if (body && (type === 'task_created' || type === 'task_overdue')) {
    return body;
  }

  if (body && type === 'task_comment') {
    if (/оставил|написал|прокомментировал/i.test(body)) return body;
    return `${actor} оставил(а) комментарий${taskQ}: ${body}`;
  }

  if (type === 'task_updated') {
    return `${actor} обновил(а) задачу${quotedTaskTitle(notif, body)}`;
  }

  if (type === 'task_repeat_updated') {
    return `${actor} обновил(а) повторение задачи${quotedTaskTitle(notif, body)}`;
  }

  if (body) return body;

  switch (type) {
    case 'task_created':
      return `${actor} поставил(а) задачу${taskQ}`;
    case 'task_status_changed':
      return `${actor} изменил(а) статус задачи${taskQ}`;
    case 'task_updated':
      return `${actor} обновил(а) задачу${taskQ}`;
    case 'task_comment':
      return `${actor} оставил(а) комментарий${taskQ}`;
    case 'task_overdue':
      return `Система напоминает, что задача${taskQ} просрочена`;
    case 'task_repeat_updated':
      return `${actor} обновил(а) повторение задачи${taskQ}`;
    default:
      return String(notif?.title || 'Новое событие').trim();
  }
}

function normalizeBattleEvent(payload) {
  const source = payload?.event || payload?.main_event || payload?.settings || {};
  if (!source?.is_actual) return null;
  const title = String(source?.title || '').trim() || 'Событие сейчас активно';
  const description = String(source?.description || '').trim();
  return { title, description };
}

/* ===== Helpers + Donut компонент ===== */
function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function pct(n) { return Math.round(n); }
const todayKey = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD

// Очистка истории прогресса старше maxDays дней
function pruneOldProgress(maxDays = 7) {
  try {
    const today = new Date(todayKey()); // полночь UTC для сравнения по датам YYYY-MM-DD
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      const m = k.match(/^progress_day_(\d{4}-\d{2}-\d{2})_/);
      if (m) keys.push({ k, dateStr: m[1] });
    }
    const toDelete = [];
    for (const { k, dateStr } of keys) {
      const d = new Date(dateStr);
      if (isNaN(d)) continue;
      const diffDays = Math.floor((today - d) / (1000 * 60 * 60 * 24));
      if (diffDays > maxDays) toDelete.push(k);
    }
    toDelete.forEach((k) => localStorage.removeItem(k));
  } catch { }
}

function Donut({ percent = 0, label = "выполнено" }) {
  const R = 30;                      // радиус
  const C = 2 * Math.PI * R;         // длина окружности
  const p = clamp01(percent / 100);

  // анимация: dashoffset от полного периметра к нужной величине
  const dash = C;                     // весь периметр
  const offset = C * (1 - p);         // скрытая часть дуги

  return (
    <svg className={s.donut} viewBox="0 0 80 80" aria-label={`Прогресс ${pct(percent)}%`}>
      <g transform="translate(40,40)">
        {/* фон */}
        <circle r={R} cx="0" cy="0" fill="none" stroke="#ead8ff" strokeWidth="12" />
        {/* прогресс */}
        <circle
          r={R}
          cx="0"
          cy="0"
          fill="none"
          stroke="#BD23DB"
          strokeWidth="12"
          strokeLinecap="butt"                 // прямые концы дуги
          strokeDasharray={dash}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease' }}  // плавная анимация заполнения диаграммы
          transform="rotate(-90)"
        />
        {/* текст */}
        <text textAnchor="middle" className={s.donutText}>{pct(percent)}%</text>
        <text y="10" textAnchor="middle" fontSize="7">{label}</text>
      </g>
    </svg>
  );
}

function renderInfobarSegment(s, { type, count, label }) {
  if (count <= 0) return null;

  const hint = `${label}: ${count}`;

  const typeClass = {
    important: s.infobarImportant,
    regular: s.infobarRegular,
    overdue: s.infobarOverdue,
    deadline: s.infobarDeadline,
  }[type];

  const wrapMod = {
    important: s.infobarSegmentWrapImportant,
    regular: s.infobarSegmentWrapRegular,
    overdue: s.infobarSegmentWrapOverdue,
    deadline: s.infobarSegmentWrapDeadline,
  }[type];

  return (
    <div
      key={`${type}-${hint}`}
      className={`${s.infobarSegmentWrap} ${wrapMod}`}
      style={{ flexGrow: count }}
      tabIndex={0}
      aria-label={hint}
    >
      <div className={`${s.infobarSegment} ${typeClass}`} />
      <span className={s.infobarSegmentTip} role="tooltip">{hint}</span>
    </div>
  );
}

function NotificationsInfographic({
  totalUnreadChats,
  totalImportantChats,
  totalUnreadNews,
  totalImportantNews,
  totalTasks,
  totalDeadlineTasks,
  totalOverdueTasks = 0,
}) {
  const chatsRegular = Math.max(0, totalUnreadChats - totalImportantChats);
  const newsRegular = Math.max(0, totalUnreadNews - totalImportantNews);
  const tasksOverdue = Math.max(0, totalOverdueTasks);
  const tasksDeadline = Math.max(0, totalDeadlineTasks);
  const tasksRegular = Math.max(0, totalTasks - tasksDeadline - tasksOverdue);

  const segmentLabels = {
    chats: { important: 'Важные сообщения', regular: 'Непрочитанные сообщения' },
    news: { important: 'Важные новости', regular: 'Непрочитанные новости' },
  };

  const renderColumn = (key, label, importantCount, regularCount, alwaysCheck = false) => {
    const hasUnread = (importantCount + regularCount) > 0;
    const labels = segmentLabels[key] || { important: 'Важные', regular: 'Непрочитанные' };

    // если нет непрочитанных или логика ещё не реализована — зелёная галочка
    if (alwaysCheck || !hasUnread) {
      return (
        <div key={key} className={s.infobarCol}>
          <div className={`${s.infobarCheckWrapper} ${s.infobarCheckWrapperHover}`} tabIndex={0} aria-label="Нет непрочитанных">
            <div className={s.infobarCheck}>
              <DoneIcon className={s.infobarCheckIcon} />
            </div>
            <span className={s.infobarSegmentTip} role="tooltip">Нет непрочитанных</span>
          </div>
          <div className={s.infobarLabel}>{label}</div>
        </div>
      );
    }

    // есть непрочитанные — рисуем столбик из двух сегментов:
    // красный — важные, светло-фиолетовый — обычные
    return (
      <div key={key} className={s.infobarCol}>
        <div className={s.infobarTrack}>
          {renderInfobarSegment(s, { type: 'important', count: importantCount, label: labels.important })}
          {renderInfobarSegment(s, { type: 'regular', count: regularCount, label: labels.regular })}
        </div>
        <div className={s.infobarLabel}>{label}</div>
      </div>
    );
  };

  const renderTasksColumn = () => {
    const hasUnread = (tasksOverdue + tasksDeadline + tasksRegular) > 0;

    if (!hasUnread) {
      return renderColumn('tasks', 'Задачи', 0, 0);
    }

    return (
      <div key="tasks" className={s.infobarCol}>
        <div className={s.infobarTrack}>
          {renderInfobarSegment(s, { type: 'overdue', count: tasksOverdue, label: 'Просроченные задачи' })}
          {renderInfobarSegment(s, { type: 'deadline', count: tasksDeadline, label: 'Дедлайн сегодня' })}
          {renderInfobarSegment(s, { type: 'regular', count: tasksRegular, label: 'Задачи без срочности' })}
        </div>
        <div className={s.infobarLabel}>Задачи</div>
      </div>
    );
  };

  return (
    <div className={s.infographicCard}>
      <div className={s.infobarRow}>
        {renderColumn('chats', 'Чаты', totalImportantChats, chatsRegular)}
        {renderColumn('news', 'Новости', totalImportantNews, newsRegular)}
        {renderTasksColumn()}
        {renderColumn('events', 'События', 0, 0, true)}
      </div>
    </div>
  );
}

/** Модалка голосования (full-screen) */
function PollVoteModal({ poll, onClose, onSubmit, currentEmployeeId }) {
  const [selected, setSelected] = useState(() => new Set());
  const [comment, setComment] = useState('');
  const multiple = !!poll?.is_multiple;

  // тип опроса и флаги UI
  const pollType = poll?.type || 'variants';
  const isEmployees = pollType === 'employees';
  const isTeams = pollType === 'teams';
  const isVariants = pollType === 'options';

  // поиск по доступным опциям (для employees/teams)
  const [search, setSearch] = useState('');
  const q = useDebouncedValue(search, 300);

  // ====== НОВОЕ: кеши аватарок сотрудников и картинок команд
  const [empAvatars, setEmpAvatars] = useState(() => ({}));
  const [teamImages, setTeamImages] = useState(() => ({}));

  // при открытии модалки синхронизируем локальный state из кэша
  useEffect(() => {
    if (poll?.type === 'employees') {
      const map = {};
      (poll?.options || []).forEach(o => {
        const id = o?.employee_id;
        const url = id ? EMP_AVATAR_CACHE.get(id) : null;
        if (id && url) map[id] = url;
      });
      setEmpAvatars(map);
    }
    if (poll?.type === 'teams') {
      const map = {};
      (poll?.options || []).forEach(o => {
        const id = o?.team_id;
        const url = id ? TEAM_IMAGE_CACHE.get(id) : null;
        if (id && url) map[id] = url;
      });
      setTeamImages(map);
    }
  }, [poll?.id, poll?.type, poll?.options]);


  // универсальный резолвер для /uploads/*
  const abs = (url) => (!url ? null : (url.startsWith('/uploads/') ? `${API_ORIGIN}${url}` : url));

  const getOptionAvatar = useCallback((opt) => {
    const direct =
      opt?.avatar_url || opt?.avatar ||
      opt?.image_url || opt?.image ||
      opt?.logo;
    if (direct) return abs(direct);

    // 1) Если у варианта есть employee_id — пробуем кэш независимо от типа опроса
    if (opt?.employee_id && empAvatars[opt.employee_id]) {
      return abs(empAvatars[opt.employee_id]);
    }
    // 2) Если есть team_id — пробуем кэш изображений команд
    if (opt?.team_id && teamImages[opt.team_id]) {
      return abs(teamImages[opt.team_id]);
    }
    return null;
  }, [empAvatars, teamImages]);


  // НОВОЕ: при открытии модалки команд — подгрузить все команды (image_url)
  useEffect(() => {
    if (!isTeams) return;
    // если уже есть в кэше — просто отдадим из него
    const inCache = {};
    let hasAll = true;
    (poll?.options || []).forEach(o => {
      const id = o?.team_id;
      const url = id ? TEAM_IMAGE_CACHE.get(id) : null;
      if (id && url) inCache[id] = url;
      if (id && !url) hasAll = false;
    });
    setTeamImages(inCache);
    if (hasAll) return;

    let alive = true;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/structure/teams`);
        const list = r.ok ? await r.json() : [];
        (Array.isArray(list) ? list : []).forEach(t => {
          if (t?.id) TEAM_IMAGE_CACHE.set(t.id, t.image_url || t.logo || t.image || null);
        });
        if (!alive) return;
        const map = {};
        (poll?.options || []).forEach(o => {
          const id = o?.team_id;
          const url = id ? TEAM_IMAGE_CACHE.get(id) : null;
          if (id && url) map[id] = url;
        });
        setTeamImages(map);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [isTeams, poll?.id, poll?.options]);


  // НОВОЕ: подкачка аватаров сотрудников «по мере надобности»
  useEffect(() => {
    // для options тоже грузим аватарки, если среди вариантов есть employee_id
    const hasEmployeeOptions =
      Array.isArray(poll?.options) && poll.options.some(o => o?.employee_id);
    if (!(isEmployees || hasEmployeeOptions)) return;

    // какие id нужны именно для этого опроса
    const ids = (poll?.options || [])
      .map(o => o?.employee_id)
      .filter(Boolean);

    // из кэша — в state
    const cached = {};
    ids.forEach(id => {
      const url = EMP_AVATAR_CACHE.get(id);
      if (url) cached[id] = url;
    });
    setEmpAvatars(cached);

    // что ещё нужно догрузить
    const need = ids.filter(id => !EMP_AVATAR_CACHE.has(id));
    if (need.length === 0) return;

    let aborted = false;

    const loadOne = async (id) => {
      try {
        const r = await apiFetch(`${API_URL}/employees/${id}`);
        if (!r.ok) return;
        const j = await r.json();
        const url = j?.avatar_url || j?.avatar || null;
        if (url) {
          EMP_AVATAR_CACHE.set(id, url);
          if (!aborted) {
            // точечно обновим state (без ребилда массива зависимостей)
            setEmpAvatars(prev => (prev[id] ? prev : { ...prev, [id]: url }));
          }
        }
      } catch {/* ignore */ }
    };

    // ограничение параллельности (например, по 5 запросов)
    (async () => {
      const queue = [...need];
      const WORKERS = 5;
      const workers = Array.from({ length: WORKERS }, async () => {
        while (queue.length && !aborted) {
          const id = queue.shift();
          await loadOne(id);
        }
      });
      await Promise.all(workers);
    })();

    return () => { aborted = true; };
  }, [isEmployees, poll?.id, poll?.options]);


  // базовые опции приходят с бэка: [{id, label, ...}]
  const baseOptions = useMemo(
    () => Array.isArray(poll?.options) ? poll.options : [],
    [poll]
  );

  // фильтрация по label
  const filtered = useMemo(() => {
    if (!q) return baseOptions;
    const needle = q.trim().toLowerCase();
    return baseOptions.filter(o => (o?.label || '').toLowerCase().includes(needle));
  }, [baseOptions, q]);

  // Показ в UI:
  // если одиночный опрос и выбран ровно 1 вариант — показываем только его
  const visibleOptions = useMemo(() => {
    if (!multiple && selected.size === 1) {
      const only = selected.values().next().value;
      return baseOptions.filter(o => o.id === only);
    }
    return filtered;
  }, [multiple, selected, baseOptions, filtered]);


  const toggle = (id) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (multiple) {
        s.has(id) ? s.delete(id) : s.add(id);
      } else {
        // одиночный выбор: повторный клик — снять выбор
        if (s.has(id)) s.clear();
        else { s.clear(); s.add(id); }
      }
      return s;
    });
  };


  // Отключаем отправку, если не выбран вариант ИЛИ комментарий пустой
  const canSend = (selected.size > 0) && (comment.trim().length > 0);

  const send = () => {
    onSubmit?.({
      option_ids: Array.from(selected),
      comment: comment?.trim() || null,
    });
  };


  const modal = (
    <div
      className={`${s.modalBackdrop} ${s.pollVoteModalBackdrop}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className={s.modalCard} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalHeader}>
          <strong>Голосование «{poll?.title || ''}»</strong>
          <button className={s.sortCloseBtn} onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <div className={s.modalBody}>
          {poll?.description && (
            <div className={s.pollBlock}>
              <div className={s.pollDescriptionWrap}>
                <div className={s.pollDescription}>
                  {poll.description}
                </div>
              </div>
            </div>
          )}

          {/* EMPLOYEES / TEAMS: поле поиска с дебаунсом + отфильтрованные опции */}
          {(isEmployees || isTeams) && (
            <>

              <div className={s.pollBlock}>
                <SearchInputComponent
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={isEmployees ? 'Поиск сотрудника…' : 'Поиск команды…'}
                  ariaLabel={isEmployees ? 'Поиск сотрудника' : 'Поиск команды'}
                />
              </div>

              <div className={s.pollBlockTitle}>Варианты</div>
              <ul className={s.cardList}>
                {visibleOptions.map(opt => (
                  <li key={opt.id} className={`${s.cardItem} ${s.voteRow}`} onClick={() => toggle(opt.id)} role="button">
                    <div className={s.head}>
                      <div className={s.msgMain}>
                        <div className={`${s.titleRow} ${s.voteRowTitle}`}>
                          <span className={s.voteChoice} aria-hidden="true">
                            {multiple
                              ? <input className={s.voteInput} type="checkbox" readOnly checked={selected.has(opt.id)} />
                              : <input className={s.voteInput} type="radio" readOnly checked={selected.has(opt.id)} />}
                          </span>
                          <Avatar
                            src={getOptionAvatar(opt)}
                            name={opt.label}
                            alt={opt.label || ''}
                            className={s.itemAvatar}
                            loading="lazy"
                          />
                          <div className={s.msgText}>
                            <strong className={s.itemTitle}>{opt.label}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
                {!filtered.length && (
                  <li className={s.cardItem}>
                    <div className={s.head}><div className={s.msgMain}>Ничего не найдено</div></div>
                  </li>
                )}
              </ul>
            </>
          )}

          {/* VARIANTS: как раньше — список без поиска */}
          {isVariants && (
            <>
              <div className={s.pollBlockTitle}>Варианты</div>
              <ul className={s.cardList}>
                {visibleOptions.map(opt => {
                  const isEmployeeOpt = Boolean(opt.employee_id);
                  const isSelf = isEmployeeOpt && String(opt.employee_id) === String(currentEmployeeId);
                  const src = isEmployeeOpt ? getOptionAvatar(opt) : null;
                  const onPick = () => {
                    if (isSelf) { alert('Нельзя голосовать за себя'); return; }
                    toggle(opt.id);
                  };
                  return (
                    <li
                      key={opt.id}
                      className={`${s.cardItem} ${s.voteRow}`}
                      onClick={onPick}
                      role="button"
                    >
                      <div className={s.head}>
                        <div className={s.msgMain}>
                          <div className={`${s.titleRow} ${s.voteRowTitle}`}>
                            <span className={s.voteChoice} aria-hidden="true">
                              {multiple
                                ? <input className={s.voteInput} type="checkbox" readOnly checked={selected.has(opt.id)} />
                                : <input className={s.voteInput} type="radio" readOnly checked={selected.has(opt.id)} />}
                            </span>
                            {isEmployeeOpt ? (
                              <Avatar
                                src={src}
                                name={opt.label}
                                alt={opt.label || ''}
                                className={s.itemAvatar}
                                loading="lazy"
                              />
                            ) : null}
                            <div className={s.msgText}>
                              <strong className={s.itemTitle}>{opt.label}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          <div className={s.pollBlockTitle}>Комментарий <span style={{ color: '#C01048', fontWeight: 400 }}>*</span></div>
          <Textarea
            className={s.pollCommentInput}
            rows={4}
            placeholder="Оставьте комментарий к вашему голосу (это обязательно)"
            value={comment}
            ariaLabel="Обязательный комментарий к голосу"
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <div className={s.modalFooter}>
          <button className={s.sortResetBtn} onClick={onClose}>Позже</button>
          <button className={s.sortApplyBtn} onClick={send} disabled={!canSend}>Проголосовать</button>
        </div>
      </div>
    </div>
  );

  return modal;
}

/**
 * Элемент ленты «Пульс».
 *
 * Бэкенд может вернуть ДВА варианта:
 * 1) Сырые непрочитанные сообщения (одно сообщение = один элемент).
 *    Тогда мы АГРЕГИРУЕМ по chat_id и строим +unread_count сами.
 * 2) Уже пред-агрегированные элементы по чатам со своими last_* и unread_count.
 */
export default function Plus() {
  const employeeId = useEmployeeId();
  const { role } = useRoleState();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [items, setItems] = useState([]); // сырые данные «как пришли» с бэка (сообщения)
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const socketRef = useRef(null);

  // непрочитанные новости
  const [unreadNews, setUnreadNews] = useState([]);
  const [taskNotifications, setTaskNotifications] = useState([]);
  const [loadingTaskNotif, setLoadingTaskNotif] = useState(true);
  const [deadlineTasks, setDeadlineTasks] = useState([]);
  const [loadingDeadlineTasks, setLoadingDeadlineTasks] = useState(true);

  // ===== ДОБАВЛЕНО: активные голосования + модалка
  const [activePolls, setActivePolls] = useState([]);
  const [activePollIndex, setActivePollIndex] = useState(0);
  const [pollModal, setPollModal] = useState({ open: false, poll: null });
  const [activeBattleEvent, setActiveBattleEvent] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/polls/active?t=${Date.now()}`, { cache: 'no-store' });
        const j = r.ok ? await r.json() : [];
        if (alive) setActivePolls(Array.isArray(j) ? j : []);
      } catch {/* ignore */ }
    })();
    return () => { alive = false; };
  }, []);
  // ===== /ДОБАВЛЕНО

  useEffect(() => {
    setActivePollIndex((prev) => {
      if (!activePolls.length) return 0;
      return Math.min(Math.max(prev, 0), activePolls.length - 1);
    });
  }, [activePolls.length]);

  // фильтры/поиск по уведомлениям
  const [filterTab, setFilterTab] = useState('all'); // 'all' | 'important' | 'deadline' | 'expired'
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // настройки сортировки
  const [sortDirection, setSortDirection] = useState('new_first'); // 'new_first' | 'old_first'
  const [priorityFirst, setPriorityFirst] = useState(false);

  // модалка сортировки
  const [isSortModalOpen, setSortModalOpen] = useState(false);
  const [draftSortDirection, setDraftSortDirection] = useState('new_first');
  const [draftPriorityFirst, setDraftPriorityFirst] = useState(false);

  // те же 4 вкладки, что и раньше
  const filterTabsConfig = [
    { key: 'all', label: 'Все', disabled: false },
    { key: 'important', label: 'Важные', disabled: false },
    { key: 'deadline', label: 'Дедлайн', disabled: false },
    { key: 'expired', label: 'Истекло', disabled: false },
  ];

  const isTaskCardsView = filterTab === 'important' || filterTab === 'deadline' || filterTab === 'expired';
  const hideFeedSections = filterTab === 'deadline' || filterTab === 'expired';

  const activeFilterIndex = Math.max(
    0,
    filterTabsConfig.findIndex((t) => t.key === filterTab),
  );
  const filtersIndicatorStyle = {
    width: `${100 / filterTabsConfig.length}%`,
    transform: `translateX(${activeFilterIndex * 100}%)`,
  };

  const openSortModal = useCallback(() => {
    setDraftSortDirection(sortDirection);
    setDraftPriorityFirst(priorityFirst);
    setSortModalOpen(true);
  }, [sortDirection, priorityFirst]);

  const closeSortModal = useCallback(() => setSortModalOpen(false), []);

  const applySortSettings = useCallback(() => {
    setSortDirection(draftSortDirection);
    setPriorityFirst(draftPriorityFirst);
    setSortModalOpen(false);
  }, [draftSortDirection, draftPriorityFirst]);

  const resetSortSettings = useCallback(() => {
    setDraftSortDirection('new_first');
    setDraftPriorityFirst(false);
    setSortDirection('new_first');
    setPriorityFirst(false);
  }, []);

  // === состояние боковой панели ===
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const me = useMe(employeeId);
  const employeeTaskStatsIds = useMemo(
    () => (employeeId ? [employeeId] : []),
    [employeeId]
  );
  const employeeTaskStatsMap = useEmployeeTaskStats(employeeTaskStatsIds);
  const touchStartRef = useRef({ x: 0, y: 0, active: false });

  // дебаунс поиска по уведомлениям (300 мс)
  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((v) => !v), []);

  // === состояние сворачивания секций ===
  const [isChatsOpen, setChatsOpen] = useState(true);
  const [isNewsOpen, setNewsOpen] = useState(true);
  const [isTasksOpen, setTasksOpen] = useState(true);

  // ---- ДИПЛИНКИ для пушей: /plus?open_chat=<id>&m=<msgId> -> /chats?open=<id>&m=<msgId> ----
  useEffect(() => {
    const sp = new URLSearchParams(search);
    const taskId = sp.get('task');
    if (taskId) {
      const fromPush = sp.get('from_push');
      navigate(
        `/tasks?task=${encodeURIComponent(taskId)}${fromPush ? '&from_push=1' : ''}`,
        { replace: true }
      );
      return;
    }
    const chatId = sp.get('open_chat') || sp.get('open'); // поддерживаем обе схемы
    const msgId = sp.get('m') || sp.get('msg');          // и оба ключа для сообщения
    if (chatId) {
      navigate(`/chats?open=${encodeURIComponent(chatId)}${msgId ? `&m=${encodeURIComponent(msgId)}` : ''}`, { replace: true });
    }
  }, [search, navigate]);
  // -------------------------------------------------------------------------------

  // ДОБАВЛЕНО: отдельные флаги загрузки для готовности расчёта
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingNews, setLoadingNews] = useState(true);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!loadingChats && !loadingNews && !loadingTaskNotif && !loadingDeadlineTasks) setReady(true);
  }, [loadingChats, loadingNews, loadingTaskNotif, loadingDeadlineTasks]);

  useEffect(() => {
    const body = document.body;

    if (pollModal.open) {
      body.classList.add('poll-modal-open');
    } else {
      body.classList.remove('poll-modal-open');
    }

    return () => body.classList.remove('poll-modal-open');
  }, [pollModal.open]);

  useEffect(() => {
    const body = document.body;
    if (isDrawerOpen) {
      body.classList.add('drawer-open');
    } else {
      body.classList.remove('drawer-open');
    }
    return () => body.classList.remove('drawer-open');
  }, [isDrawerOpen]);

  useEffect(() => {
    const shouldLockScroll = pollModal.open || isDrawerOpen;
    if (!shouldLockScroll) return undefined;

    const body = document.body;
    const html = document.documentElement;
    const scrollY = window.scrollY || document.documentElement.scrollTop;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      const top = body.style.top;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      html.style.overflow = '';
      const y = parseInt(top || '0', 10) * -1;
      window.scrollTo(0, y);
    };
  }, [pollModal.open, isDrawerOpen]);

  // загрузка непрочитанного списка сообщений
  const loadUnread = async (showSpinner = false) => {
    try {
      setErr('');
      if (showSpinner) setLoading(true);   // ← спиннер только когда нужно
      setLoadingChats(true);
      const res = await apiFetch(`${API_URL}/chats/unread_feed`);
      if (!res.ok) throw new Error(`Failed to load unread: ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.message || 'Не удалось загрузить ленту');
    } finally {
      if (showSpinner) setLoading(false);  // ← и выключаем только если включали
      setLoadingChats(false);
    }
  };

  // загрузка непрочитанных новостей
  const loadUnreadNews = async () => {
    try {
      setLoadingNews(true);
      const res = await apiFetch(`${API_URL}/news/unread_feed`);
      if (!res.ok) throw new Error(`Failed to load unread news: ${res.status}`);
      const data = await res.json();
      setUnreadNews(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    } finally {
      setLoadingNews(false);
    }
  };

  const loadUnreadTaskNotifications = useCallback(async () => {
    try {
      setLoadingTaskNotif(true);
      const res = await apiFetch(`${API_URL}/tasks/notifications?limit=30&unread_only=true`);
      if (!res.ok) throw new Error(`Failed to load task notifications: ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.items) ? data.items : [];
      setTaskNotifications(list.filter((n) => !n.is_read));
    } catch {
      setTaskNotifications([]);
    } finally {
      setLoadingTaskNotif(false);
    }
  }, []);

  const loadDeadlineTasks = useCallback(async () => {
    try {
      setLoadingDeadlineTasks(true);
      const res = await apiFetch(`${API_URL}/tasks/my?limit=100&sort=deadline`);
      if (!res.ok) throw new Error(`Failed to load tasks: ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.items) ? data.items : [];
      setDeadlineTasks(list.filter((t) => t.status !== 'archive' && t.status !== 'done'));
    } catch {
      setDeadlineTasks([]);
    } finally {
      setLoadingDeadlineTasks(false);
    }
  }, []);

  const loadActiveBattleEvent = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_URL}/battles/overview`);
      if (!res.ok) {
        setActiveBattleEvent(null);
        return;
      }
      const payload = await res.json();
      setActiveBattleEvent(normalizeBattleEvent(payload));
    } catch {
      setActiveBattleEvent(null);
    }
  }, []);

  // подписка на сокеты
  useEffect(() => {
    loadUnread(true);
    loadUnreadNews();
    loadUnreadTaskNotifications();
    loadDeadlineTasks();
    loadActiveBattleEvent();

    if (!employeeId) return;

    const socket = io(SOCKET_URL, {
      path: '/ws/socket.io',
      transports: ['websocket'],
      withCredentials: true,
      auth: (cb) => {
        cb({
          token: getAccessToken(),
        });
      },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      // сервер сам определяет employee_id по токену/cookie
    });

    const refreshUnreadChats = () => {
      loadUnread(false).catch(() => { });
    };

    socket.on('new_message', (payload) => {
      const { chat_id, message } = (payload || {});
      if (!message || !chat_id) return;
      if (Number(message.sender_id) === Number(employeeId)) return;
      refreshUnreadChats();
    });

    socket.on('chat_sidebar_update', (payload) => {
      if (!payload?.chat_id) return;
      if (Number(payload.sender_id) === Number(employeeId)) return;
      refreshUnreadChats();
    });

    socket.on('news_published', () => {
      loadUnreadNews();
    });

    const refreshTaskNotifications = () => {
      loadUnreadTaskNotifications().catch(() => { });
    };

    const refreshDeadlineTasks = () => {
      loadDeadlineTasks().catch(() => { });
    };

    socket.on('tasks:update', (payload) => {
      const eventType = String(payload?.type || '').toLowerCase();
      if (payload?.notification_id || eventType === 'task_notifications_read') {
        refreshTaskNotifications();
      }
      refreshDeadlineTasks();
    });
    socket.on('task_comment', () => {
      refreshTaskNotifications();
      refreshDeadlineTasks();
    });
    socket.on('task_comment_updated', () => {
      refreshTaskNotifications();
      refreshDeadlineTasks();
    });
    socket.on('task_comment_deleted', () => {
      refreshTaskNotifications();
      refreshDeadlineTasks();
    });

    socket.on('chat_read', (payload) => {
      if (!payload?.chat_id) return;
      if (Number(payload.employee_id) !== Number(employeeId)) return;
      loadUnread(false).catch(() => { });
    });

    return () => socket.disconnect();
  }, [employeeId, loadActiveBattleEvent, loadDeadlineTasks, loadUnreadTaskNotifications]);

  // Закрытие панели по Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeDrawer(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeDrawer]);

  // обработка свайпов:
  // - открыть: свайп вправо (dx > 40, dy < 30) с ЛЮБОГО места экрана
  // - закрыть: свайп влево при открытой панели (dx < -40, dy < 30)
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;

    // Всегда включаем трекинг жеста, чтобы разрешить открывающий свайп откуда угодно
    touchStartRef.current = { x: t.clientX, y: t.clientY, active: true };
  };

  const onTouchMove = (e) => {
    const st = touchStartRef.current;
    if (!st.active) return;
    const t = e.touches?.[0]; if (!t) return;

    const dx = t.clientX - st.x;
    const dy = t.clientY - st.y;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    if (absDy > absDx && absDy > 8) {
      st.active = false;
      return;
    }

    // открыть по свайпу вправо откуда угодно
    if (!isDrawerOpen && dx > 50 && absDy < 40) {
      openDrawer();
      st.active = false;
      return;
    }

    // закрыть по свайпу влево при открытой панели
    if (isDrawerOpen && dx < -40 && absDy < 30) {
      closeDrawer();
      st.active = false;
      return;
    }
  };

  const onTouchEnd = () => { touchStartRef.current.active = false; };

  // унификация входных данных в единый вид
  const feed = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const looksAggregated = items.some(
      (x) =>
        'unread_count' in x ||
        'last_message_id' in x ||
        'last_timestamp' in x
    );

    if (looksAggregated) {
      const normalized = items.map((x) => {
        const lastId = x.last_message_id ?? x.message_id;
        const lastTime = x.last_timestamp ?? x.timestamp;
        return {
          chat_id: x.chat_id,
          chat_type: x.chat_type,
          // ⬇️ важная правка: если chat_name отсутствует, используем title
          chat_name: x.chat_name ?? x.title,
          message_id: lastId,
          sender_id: x.last_sender_id ?? x.sender_id,
          sender_name: x.last_sender_name ?? x.sender_name,
          content: x.last_content ?? x.content,
          timestamp: lastTime,
          unread_count: Number(x.unread_count || 1),
          last_message_text: x.last_message_text ?? null,
          last_has_attachments:
            (x.last_has_attachments ?? (Array.isArray(x.last_attachments) && x.last_attachments.length > 0)) || false,
          // флаг важности последнего сообщения (если бэк его отдаёт)
          is_important: Boolean(x.last_is_important ?? x.is_important ?? false),
        };
      });

      return normalized.sort((a, b) => {
        const ta = new Date(a.timestamp).getTime() || 0;
        const tb = new Date(b.timestamp).getTime() || 0;
        return tb - ta;
      });
    }

    // сырые сообщения — агрегируем по чатам// сырые сообщения — агрегируем по чатам
    const byChat = new Map();
    for (const m of items) {
      const key = m.chat_id;
      const cur = byChat.get(key);
      if (!cur) {
        byChat.set(key, {
          chat_id: m.chat_id,
          chat_type: m.chat_type,
          chat_name: m.chat_type === 'group' ? m.chat_name : undefined,
          message_id: m.message_id,
          sender_id: m.sender_id,
          sender_name: m.sender_name,
          content: m.content,
          timestamp: m.timestamp,
          unread_count: 1,
          // флаг важности последнего сообщения в чате
          is_important: Boolean(m.is_important),
        });
      } else {
        const tNew = new Date(m.timestamp).getTime() || 0;
        const tOld = new Date(cur.timestamp).getTime() || 0;
        if (tNew > tOld) {
          cur.message_id = m.message_id;
          cur.sender_id = m.sender_id;
          cur.sender_name = m.sender_name;
          cur.content = m.content;
          cur.timestamp = m.timestamp;
          // обновляем флаг под последнее сообщение
          cur.is_important = Boolean(m.is_important);
        }
        cur.unread_count += 1;
      }
    }

    return Array.from(byChat.values()).sort((a, b) => {
      const ta = new Date(a.timestamp).getTime() || 0;
      const tb = new Date(b.timestamp).getTime() || 0;
      return tb - ta;
    });
  }, [items]);

  // отфильтрованный список чатов с учётом вкладки и поиска
  const filteredFeed = useMemo(() => {
    if (!Array.isArray(feed) || feed.length === 0) return [];

    let res = feed;

    // вкладка "Важные" — оставляем только важные сообщения
    if (filterTab === 'important') {
      res = res.filter((it) => it.is_important);
    }

    const q = debouncedSearch;
    if (q) {
      res = res.filter((it) => {
        const text = [
          it.chat_name,
          it.sender_name,
          it.title,
          it.content,
        ].filter(Boolean).join(' ').toLowerCase();

        return text.includes(q);
      });
    }

    // сортировка: приоритет сверху (если включен) + направление по дате
    const sorted = [...res].sort((a, b) => {
      if (priorityFirst) {
        const pa = a.is_important ? 1 : 0;
        const pb = b.is_important ? 1 : 0;
        if (pa !== pb) return pb - pa; // важные сначала
      }

      const ta = new Date(a.timestamp).getTime() || 0;
      const tb = new Date(b.timestamp).getTime() || 0;

      if (sortDirection === 'old_first') return ta - tb; // сначала старые
      return tb - ta;                                     // сначала новые
    });

    return sorted;
  }, [feed, filterTab, debouncedSearch, sortDirection, priorityFirst]);

  // суммарные счётчики для заголовков
  const totalUnreadChats = useMemo(
    () => feed.reduce((sum, it) => sum + (Number(it.unread_count) || 1), 0),
    [feed]
  );

  const totalImportantChats = useMemo(
    () =>
      feed.reduce(
        (sum, it) =>
          sum + (it.is_important ? (Number(it.unread_count) || 1) : 0),
        0
      ),
    [feed]
  );

  const filteredTaskNotifications = useMemo(() => {
    if (!Array.isArray(taskNotifications) || taskNotifications.length === 0) return [];

    let res = taskNotifications;
    const q = debouncedSearch;
    if (q) {
      res = res.filter((n) => {
        const text = [
          n.task_title,
          formatTaskNotificationEvent(n),
          n.title,
          n.body,
          n.actor?.name,
        ].filter(Boolean).join(' ').toLowerCase();
        return text.includes(q);
      });
    }

    const sorted = [...res].sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime() || 0;
      const tb = new Date(b.created_at || 0).getTime() || 0;
      return (sortDirection === 'old_first') ? (ta - tb) : (tb - ta);
    });

    return groupTaskNotificationsByTask(sorted);
  }, [taskNotifications, debouncedSearch, sortDirection]);

  const totalTaskNotifications = taskNotifications.length;

  const totalDeadlineTodayTasks = useMemo(
    () => deadlineTasks.filter((task) => (
      task.deadline_at
      && task.is_deadline_today
      && !task.is_overdue
      && isActiveDeadlineTask(task)
    )).length,
    [deadlineTasks],
  );

  const tasksInfographicStats = useMemo(() => {
    let overdue = 0;
    let deadline = 0;
    let regular = 0;

    for (const task of deadlineTasks) {
      if (!isActiveDeadlineTask(task)) continue;
      if (task.is_overdue) {
        overdue += 1;
      } else if (task.deadline_at && task.is_deadline_today) {
        deadline += 1;
      } else {
        regular += 1;
      }
    }

    return {
      total: overdue + deadline + regular,
      overdue,
      deadline,
      regular,
    };
  }, [deadlineTasks]);

  const taskCardsByTab = useMemo(() => ({
    important: deadlineTasks.filter((task) => task.is_important && isActiveDeadlineTask(task)),
    deadline: deadlineTasks.filter((task) => (
      task.deadline_at
      && task.is_deadline_today
      && !task.is_overdue
      && isActiveDeadlineTask(task)
    )),
    expired: deadlineTasks.filter((task) => (
      task.deadline_at
      && task.is_overdue
      && isActiveDeadlineTask(task)
    )),
  }), [deadlineTasks]);

  const taskCardsEmptyText = {
    important: 'Нет важных задач',
    deadline: 'Нет задач с дедлайном',
    expired: 'Нет просроченных задач',
  };

  const filteredDisplayedTaskCards = useMemo(() => {
    if (!isTaskCardsView) return [];

    let res = taskCardsByTab[filterTab] || [];
    const q = debouncedSearch;

    if (q) {
      res = res.filter((task) => {
        const text = [
          task.title,
          formatTaskDeadline(task.deadline_at),
          TASK_STATUS_LABELS[task.status],
        ].filter(Boolean).join(' ').toLowerCase();
        return text.includes(q);
      });
    }

    return [...res].sort((a, b) => {
      if (filterTab === 'important') {
        const ta = new Date(a.created_at || a.updated_at || 0).getTime() || 0;
        const tb = new Date(b.created_at || b.updated_at || 0).getTime() || 0;
        return (sortDirection === 'old_first') ? (ta - tb) : (tb - ta);
      }

      const ta = new Date(a.deadline_at || 0).getTime() || 0;
      const tb = new Date(b.deadline_at || 0).getTime() || 0;
      if (filterTab === 'expired') {
        return (sortDirection === 'old_first') ? (ta - tb) : (tb - ta);
      }
      return (sortDirection === 'old_first') ? (tb - ta) : (ta - tb);
    });
  }, [
    taskCardsByTab,
    debouncedSearch,
    filterTab,
    isTaskCardsView,
    sortDirection,
  ]);

  const totalDisplayedTaskCards = filteredDisplayedTaskCards.length;

  const totalUnreadNews = unreadNews.length;

  const totalImportantNews = useMemo(
    () =>
      unreadNews.reduce(
        (sum, n) => sum + (n.is_top ? 1 : 0),
        0
      ),
    [unreadNews]
  );

  // отфильтрованный список новостей
  const filteredNews = useMemo(() => {
    if (!Array.isArray(unreadNews) || unreadNews.length === 0) return [];

    let res = unreadNews;

    if (filterTab === 'important') {
      res = res.filter((n) => n.is_top);
    }

    const q = debouncedSearch;
    if (q) {
      res = res.filter((n) => {
        const text = [n.title, n.rubric_title, n.rubric, n.category]
          .filter(Boolean).join(' ').toLowerCase();
        return text.includes(q);
      });
    }

    const sorted = [...res].sort((a, b) => {
      if (priorityFirst) {
        const pa = (a.is_top || a.is_birthday) ? 1 : 0;
        const pb = (b.is_top || b.is_birthday) ? 1 : 0;
        if (pa !== pb) return pb - pa;
      }
      const ta = new Date(a.created_at || a.date).getTime() || 0;
      const tb = new Date(b.created_at || b.date).getTime() || 0;
      return (sortDirection === 'old_first') ? (ta - tb) : (tb - ta);
    });

    return sorted;
  }, [unreadNews, filterTab, debouncedSearch, sortDirection, priorityFirst]);

  /* ======= Прогресс «за день» ======= */
  const [dayId, setDayId] = useState(todayKey());            // YYYY-MM-DD
  const lsKey = (suffix) => `progress_day_${dayId}_${suffix}`;

  const [dayStart, setDayStart] = useState(() => {
    try { const v = localStorage.getItem(lsKey('start')); return v == null ? null : Number(v) || 0; } catch { return null; }
  });
  const [dayArrivals, setDayArrivals] = useState(() => {
    try { return Number(localStorage.getItem(lsKey('arrivals')) || '0') || 0; } catch { return 0; }
  });
  const [dayRead, setDayRead] = useState(() => {
    try { return Number(localStorage.getItem(lsKey('read')) || '0') || 0; } catch { return 0; }
  });
  const [lastPending, setLastPending] = useState(() => {
    try { const v = localStorage.getItem(lsKey('last')); return v == null ? null : Number(v) || 0; } catch { return null; }
  });
  const [progressPct, setProgressPct] = useState(100);

  // Очистка данных старше 7 дней при монтировании
  useEffect(() => { pruneOldProgress(7); }, []);

  // Слежение за сменой календарного дня (в фоне/при возврате на вкладку)
  useEffect(() => {
    const tick = () => {
      const now = todayKey();
      if (now !== dayId) {
        const pendingNow = ready
          ? (
            Number(totalUnreadChats || 0)
            + Number(totalUnreadNews || 0)
            + Number(totalTaskNotifications || 0)
            + Number(totalDeadlineTodayTasks || 0)
          )
          : 0;
        const nextId = now;
        setDayId(nextId);
        const k = (sfx) => `progress_day_${nextId}_${sfx}`;
        try {
          localStorage.setItem(k('start'), String(pendingNow));
          localStorage.setItem(k('arrivals'), '0');
          localStorage.setItem(k('read'), '0');
          localStorage.setItem(k('last'), String(pendingNow));
        } catch { }
        setDayStart(pendingNow);
        setDayArrivals(0);
        setDayRead(0);
        setLastPending(pendingNow);

        // Очистить историю старше 7 дней при смене дня
        pruneOldProgress(7);
      }
    };
    const iv = setInterval(tick, 30_000); // раз в 30 секунд достаточно
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [dayId, totalUnreadChats, totalUnreadNews, totalTaskNotifications, totalDeadlineTodayTasks, ready]);

  // Основной эффект учёта: любое изменение pending → дельты в arrivals/read
  useEffect(() => {
    if (!ready) return; // ⬅️ не считаем ничего до первой загрузки данных

    const pending = (
      Number(totalUnreadChats || 0)
      + Number(totalUnreadNews || 0)
      + Number(totalTaskNotifications || 0)
      + Number(totalDeadlineTodayTasks || 0)
    );

    if (dayStart == null) {
      const startNow = pending;
      setDayStart(startNow);
      try { localStorage.setItem(lsKey('start'), String(startNow)); } catch { }
      if (lastPending == null) {
        setLastPending(startNow);
        try { localStorage.setItem(lsKey('last'), String(startNow)); } catch { }
      }
      // если на старте есть непрочитанные -> 0%, если нет -> 100%
      setProgressPct(startNow === 0 ? 100 : 0);
      return; // не считаем дельту в этот же тик
    }

    if (lastPending == null) {
      // первая фиксация lastPending после готовности данных — без дельты
      setLastPending(pending);
      try { localStorage.setItem(lsKey('last'), String(pending)); } catch { }
      // процент уже поставлен выше или будет посчитан в следующем тике
      return;
    }

    if (pending !== lastPending) {
      const delta = pending - lastPending;
      if (delta > 0) {
        setDayArrivals((v) => {
          const nv = v + delta;
          try { localStorage.setItem(lsKey('arrivals'), String(nv)); } catch { }
          return nv;
        });
      } else {
        setDayRead((v) => {
          const nv = v + (-delta);
          try { localStorage.setItem(lsKey('read'), String(nv)); } catch { }
          return nv;
        });
      }
      setLastPending(pending);
      try { localStorage.setItem(lsKey('last'), String(pending)); } catch { }
    }

    const base = dayStart + dayArrivals;
    const done = dayRead;
    const pctVal = base <= 0 ? 100 : 100 * Math.min(1, Math.max(0, done / base));
    setProgressPct(pctVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, totalUnreadChats, totalUnreadNews, totalTaskNotifications, totalDeadlineTodayTasks, dayStart, dayArrivals, dayRead, lastPending, dayId]);
  /* ================================== */

  // клик по карточке — просто открываем чат.
  // ВАЖНО: не делаем mark-read здесь, чтобы не сдвигать last_read до входа в чат.
  // Лента сама обновится по сокету/при возврате; локально можем убрать элемент из списка.
  const openChat = (it) => {
    setItems((prev) => prev.filter((x) => (x.chat_id ?? x.chat?.id) !== it.chat_id));
    navigate(`/chats?open=${it.chat_id}`);
  };

  // клик по непрочитанной новости
  const openNews = async (news) => {
    try {
      await apiFetch(`${API_URL}/news/mark-read`, {
        method: 'POST',
        body: JSON.stringify({ news_id: news.id }),
      });
      setUnreadNews((prev) => prev.filter((x) => x.id !== news.id));
    } catch { /* ignore */ }
    navigate(`/news?open=${news.id}`);
  };

  const openTask = (task) => {
    const taskId = Number(task?.id);
    if (!Number.isFinite(taskId) || taskId <= 0) return;
    navigate(`/tasks?task=${taskId}`);
  };

  const openTaskNotification = async (group) => {
    const taskId = Number(group?.task_id);
    if (!Number.isFinite(taskId) || taskId <= 0) return;

    const ids = Array.isArray(group?.notification_ids) && group.notification_ids.length
      ? group.notification_ids.map(Number).filter((id) => Number.isFinite(id) && id > 0)
      : [Number(group?.id)].filter((id) => Number.isFinite(id) && id > 0);

    if (ids.length) {
      try {
        await apiFetch(`${API_URL}/tasks/notifications/read`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notification_ids: ids }),
        });
        const idSet = new Set(ids);
        setTaskNotifications((prev) => prev.filter((x) => !idSet.has(Number(x.id))));
      } catch { /* ignore */ }
    }
    navigate(`/tasks?task=${taskId}`);
  };

  // переход по пункту меню
  const go = (to) => () => { closeDrawer(); navigate(to); };

  if (loading) return <div className="loading">Загрузка ленты…</div>;

  // НЕкликабельная полоса — просто визуал, вращается по prop open
  const ToggleLine = ({ open, ariaControls }) => (
    <div
      className={s.toggleBtn}
      aria-hidden="true"
      data-controls={ariaControls}
      // не ловим клики и курсор
      style={{ cursor: 'default', pointerEvents: 'none', padding: 0 }}
    >
      <HideLine
        className={`${s.toggleSvg} ${open ? '' : s.toggleSvgClosed}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 14, display: 'block' }}
        aria-hidden="true"
      />
    </div>
  );

  // общий стиль «аккордеона»
  const collapseStyle = (open) => ({
    display: 'grid',
    gridTemplateRows: open ? '1fr' : '0fr',
    transition: 'grid-template-rows .24s ease, opacity .24s ease',
    overflow: 'hidden',
    opacity: open ? 1 : 0,
  });

  const activePollsCount = activePolls.length;
  const currentPoll = activePolls[activePollIndex] || activePolls[0] || null;
  const hasMultiplePolls = activePollsCount > 1;

  const showPrevPoll = () => {
    if (!hasMultiplePolls) return;
    setActivePollIndex((prev) => (prev - 1 + activePollsCount) % activePollsCount);
  };

  const showNextPoll = () => {
    if (!hasMultiplePolls) return;
    setActivePollIndex((prev) => (prev + 1) % activePollsCount);
  };

  return (
    <div
      className={`${s.page} ${isDrawerOpen ? s.pageDrawerOpen : ''}`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* шапка */}
      <div className={s.topbar}>
        <div className={s.topbarRow}>
          <div className={s.titleRow}>
            <button
              className={`${s.hamburger} ${isDrawerOpen ? s.hamburgerActive : ''}`}
              aria-label={isDrawerOpen ? 'Закрыть меню' : 'Открыть меню'}
              onClick={toggleDrawer}
            >
              <span className={s.hamburgerBars} />
            </button>
            <div className={s.title}>Панель задач</div>
          </div>
          <AppLogo aria-label="Ранги" style={{ width: 140, height: 'auto' }} />
        </div>
      </div>

      {/* Верхний ряд: прогресс + инфографика уведомлений */}
      <div className={s.topCardsRow}>
        <div className={s.progressCard}>
          <p className={s.progressTitle}>Общий прогресс</p>
          <Donut percent={progressPct} label="выполнено" />
        </div>

        <NotificationsInfographic
          totalUnreadChats={totalUnreadChats}
          totalImportantChats={totalImportantChats}
          totalUnreadNews={totalUnreadNews}
          totalImportantNews={totalImportantNews}
          totalTasks={tasksInfographicStats.total}
          totalDeadlineTasks={tasksInfographicStats.deadline}
          totalOverdueTasks={tasksInfographicStats.overdue}
        />
      </div>

      {activeBattleEvent && (
        <button
          type="button"
          className={s.activeEventCard}
          onClick={() => navigate('/battles')}
          aria-label="Открыть активное событие"
        >
          <span className={s.activeEventIconWrap} aria-hidden="true">
            <TrophyIcon className={s.activeEventIcon} />
          </span>
          <span className={s.activeEventText}>
            <span className={s.activeEventTitle}>{activeBattleEvent.title}</span>
            {!!activeBattleEvent.description && (
              <span className={s.activeEventDescription}>
                {truncateText(activeBattleEvent.description, 90)}
              </span>
            )}
          </span>
          <span className={s.activeEventArrow} aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
        </button>
      )}

      {/* Виджет активного голосования под инфографикой */}
      {currentPoll && (
        <div className={`${s.votingCard} ${s.votingCardBlink}`} style={{ marginTop: 10 }}>
          <div className={`${s.votingCardInner} ${hasMultiplePolls ? s.votingCardInnerWithNav : ''}`}>
            {hasMultiplePolls && (
              <button
                type="button"
                className={s.votingNavBtn}
                onClick={showPrevPoll}
                aria-label="Предыдущее голосование"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}

            <button
              type="button"
              onClick={() => setPollModal({ open: true, poll: currentPoll })}
              className={s.votingMainBtn}
              aria-label="Открыть голосование"
            >
              <div className={s.votingText}>
                <div className={s.votingTitle}>{currentPoll.title || 'Голосование'}</div>
                <div className={s.votingHint}>
                  {hasMultiplePolls
                    ? `Нажмите, чтобы проголосовать · ${activePollIndex + 1} из ${activePollsCount}`
                    : 'Нажмите, чтобы проголосовать'}
                </div>
              </div>
              {!hasMultiplePolls && (
                <span className={s.activeEventArrow} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              )}
            </button>

            {hasMultiplePolls && (
              <button
                type="button"
                className={s.votingNavBtn}
                onClick={showNextPoll}
                aria-label="Следующее голосование"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      {/* фильтры уведомлений + поиск по тексту */}
      <div className={s.filtersBlock}>
        <div className={s.filtersTabsBar}>
          {filterTabsConfig.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={[
                s.filterTabBtn,
                filterTab === tab.key ? s.filterTabBtnActive : '',
                tab.disabled ? s.filterTabBtnDisabled : '',
              ].join(' ')}
              onClick={() => { if (!tab.disabled) setFilterTab(tab.key); }}
              disabled={tab.disabled}
            >
              {tab.label}
            </button>
          ))}

          <div className={s.filtersIndicator} style={filtersIndicatorStyle} />
        </div>

        <div className={s.searchRow}>
          <div className={s.searchInputWrap}>
            <SearchInputComponent
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по ключевым словам"
              ariaLabel="Поиск по ключевым словам"
            />
          </div>
          <button
            type="button"
            className={s.sortBtn}
            onClick={openSortModal}
            aria-label="Сортировка уведомлений"
          >
            <SortNotificationsIcon className={s.sortBtnIcon} />
          </button>
        </div>
      </div>

      {isSortModalOpen && (
        <>
          <div className={s.sortBackdrop} onClick={closeSortModal} aria-hidden="true" />
          <div className={s.sortSheet} role="dialog" aria-modal="true">
            <div className={s.sortHeader}>
              <div className={s.sortTitle}>Сортировка уведомлений</div>
              <button type="button" className={s.sortCloseBtn} onClick={closeSortModal} aria-label="Закрыть">×</button>
            </div>

            <div className={s.sortBody}>
              <div className={s.sortRow}>
                <div className={s.sortRowText}>
                  <div className={s.sortRowTitle}>Сначала новые</div>
                  <div className={s.sortRowNote}>Выключено — сначала старые</div>
                </div>
                <button
                  type="button"
                  className={`${s.sortSwitch} ${draftSortDirection === 'new_first' ? s.sortSwitchOn : ''}`}
                  onClick={() => setDraftSortDirection(draftSortDirection === 'new_first' ? 'old_first' : 'new_first')}
                  aria-pressed={draftSortDirection === 'new_first'}
                >
                  <span className={s.sortSwitchKnob} />
                </button>
              </div>

              <div className={s.sortRow}>
                <div className={s.sortRowText}>
                  <div className={s.sortRowTitle}>Показывать приоритет сверху</div>
                </div>
                <button
                  type="button"
                  className={`${s.sortSwitch} ${draftPriorityFirst ? s.sortSwitchOn : ''}`}
                  onClick={() => setDraftPriorityFirst((v) => !v)}
                  aria-pressed={draftPriorityFirst}
                >
                  <span className={s.sortSwitchKnob} />
                </button>
              </div>
            </div>

            <div className={s.sortFooter}>
              <button type="button" className={s.sortResetBtn} onClick={resetSortSettings}>Сбросить</button>
              <button type="button" className={s.sortApplyBtn} onClick={applySortSettings}>Применить</button>
            </div>
          </div>
        </>
      )}

      {/* затемнение подложки */}
      {isDrawerOpen && (
        <div className={s.backdrop} onClick={closeDrawer} role="button" aria-label="Закрыть меню" />
      )}

      {/* боковая панель */}
      <aside className={`${s.drawer} ${isDrawerOpen ? s.drawerOpen : ''}`} aria-hidden={!isDrawerOpen}>
        <div className={s.drawerBrand}>
          <SidebarLogo className={s.drawerLogo} aria-label="РАНГИ" />
        </div>

        <ul className={s.drawerList}>
          <li className={s.drawerItem} onClick={go('/about')}>
            <InfoSquareIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>Задачи 1С</span>
          </li>
          <li className={s.drawerItem} onClick={go('/tasks')}>
            <TasksIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>Задачи</span>
          </li>
          <li className={s.drawerItem} onClick={go('/structure')}>
            <AppinfoBtnIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>Структура</span>
          </li>
          <li className={s.drawerItem} onClick={go('/regulations')}>
            <ClickboardCheckIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>Регламенты</span>
          </li>
          <li className={s.drawerItem} onClick={go('/dexter')}>
            <DexterIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>Файлы</span>
          </li>
          <li className={s.drawerItem} onClick={go('/battles')}>
            <TrophyIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>Сражения</span>
          </li>
          <li className={s.drawerItem} onClick={go('/program')}>
            <FileShieldIcon className={s.drawerIconSvg} />
            <span className={s.drawerText}>О программе</span>
          </li>

          {/* админка: видно только администратору */}
          {role === 'admin' && (
            <li className={s.drawerItem} onClick={go('/admin')}>
              <AdminBtnIcon className={s.drawerIconSvg} />
              <span className={s.drawerText}>Админка</span>
            </li>
          )}
        </ul>

        {/* Мой профиль внизу сайдбара */}
        {me && (
          <div
            className={s.drawerUser}
            role="button"
            onClick={() => { closeDrawer(); navigate('/account/me'); }}
            title="Открыть мой аккаунт"
          >
            <EmployeeTaskStatsAvatar
              className={s.drawerUserAvatar}
              employeeId={employeeId}
              stats={employeeTaskStatsMap.get(Number(employeeId))}
              fetchStats={false}
              avatar={me.avatar}
              size={90}
              alt={me.name || 'avatar'}
            />
            <div className={s.drawerUserInfo}>
              <div className={s.drawerUserName}>
                <span className={s.drawerUserFirst}>{(me.name || '—').split(' ')[0] || '—'}</span>
                <span className={s.drawerUserLast}>{(me.name || '').split(' ').slice(1).join(' ')}</span>
              </div>
            </div>

            {/* Email — НИЖНЯЯ строка, тянется на всю ширину под аватаром и именем */}
            {me.email && <div className={s.drawerUserEmail}>{me.email}</div>}
          </div>
        )}
      </aside>

      {/* Непрочитанные сообщения */}
      {!hideFeedSections && (totalUnreadChats === 0 && !err ? (
        // вариант "как у проектов": слева заголовок + подпись, справа кнопка ЧАТЫ
        <div className={s.sectionRow}>
          <div>
            <div className={s.sectionTitlePlain}>Сообщения</div>
            <div className={s.sectionEmptyText}>Непрочитанных сообщений нет</div>
          </div>
          <NavLink to="/chats" className={s.sectionAction}>ЧАТЫ</NavLink>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={s.sectionTitleBtn}
            onClick={() => setChatsOpen((v) => !v)}
            aria-expanded={isChatsOpen}
            aria-controls="unread-chats"
            title={isChatsOpen ? 'Свернуть' : 'Развернуть'}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
          >
            {`Сообщения${totalUnreadChats > 0 ? ` (${totalUnreadChats})` : ''}`}
          </button>

          <div
            className={s.sectionControls}
            onClick={(e) => {
              const el = e.target;
              // если клик по ссылке "ЧАТЫ" или внутри неё — не трогаем аккордеон
              if (el.closest && el.closest('a')) return;
              setChatsOpen((v) => !v);
            }}
          >
            {totalUnreadChats > 0 && (<ToggleLine open={isChatsOpen} ariaControls="unread-chats" />)}
            <NavLink to="/chats" className={s.sectionAction}>ЧАТЫ</NavLink>
          </div>

          {err ? (
            <div style={{ margin: '2px 0 2px', background: '#fdecea', color: '#b71c1c', padding: '10px 12px', borderRadius: 8, border: '1px solid #f5c6cb' }}>
              Ошибка: {err}
            </div>
          ) : null}

          {/* список/аккордеон показываем только если есть что показывать */}
          {totalUnreadChats > 0 && (
            <div id="unread-chats" style={collapseStyle(isChatsOpen)}>
              <div style={{ minHeight: 0 }}>
                {filteredFeed.length === 0 ? (
                  <div className={s.sectionEmptyText}>Непрочитанных сообщений нет</div>
                ) : (
                  <ul className={s.cardList}>
                    {filteredFeed.map((it) => {
                      const isPrivate = it.chat_type === 'private';
                      const isGroupLike = it.chat_type === 'group' || it.chat_type === 'rank';

                      const rawTitle = isPrivate
                        ? (it.sender_name || it.chat_name || it.title || 'Сообщение')
                        : (it.chat_name || it.title || 'Чат');
                      const title = truncateText(rawTitle, 42);
                      const time = formatTime(it.timestamp);
                      const p = (it.content || '').trim();
                      const previewRaw = p || '📎 вложение';
                      const preview = previewRaw.length > 30 ? `${previewRaw.slice(0, 30)}…` : previewRaw;

                      const extraUnread = Math.max(0, (it.unread_count ?? 0) - 1);

                      const iconSrc = it.is_important ? setImportanceIcon : (isGroupLike ? unreadGroupsIcon : newMessageIcon);
                      const iconAlt = it.is_important ? 'Важное сообщение' : (isGroupLike ? 'Сообщения группы' : 'Сообщение');

                      return (
                        <li
                          key={`${it.chat_id}-${it.message_id}`}
                          className={s.cardItem}
                          onClick={() => openChat(it)}
                          role="button"
                          aria-label={`Открыть чат ${rawTitle}`}
                        >
                          <div className={s.head}>
                            <div className={s.msgMain}>
                              <div className={s.titleRow}>
                                <img className={s.itemIcon} src={iconSrc} alt={iconAlt} />
                                <div className={s.msgText}>
                                  <strong className={s.itemTitle}>{title}</strong>
                                  <div className={s.preview}>{preview}</div>
                                </div>
                              </div>
                            </div>
                            <div className={s.msgMeta}>
                              <span className={s.time}>{time}</span>
                              {extraUnread > 0 && (
                                <span className={s.badge} aria-label={`непрочитанных: ${extraUnread}`} title={`${extraUnread} непрочитанных`}>+{extraUnread}</span>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      ))}

      {/* Задачи */}
      {isTaskCardsView ? (
        totalDisplayedTaskCards === 0 ? (
          <div className={s.sectionRow}>
            <div>
              <div className={s.sectionTitlePlain}>Задачи</div>
              <div className={s.sectionEmptyText}>
                {loadingDeadlineTasks
                  ? 'Загрузка задач…'
                  : (taskCardsEmptyText[filterTab] || 'Нет задач')}
              </div>
            </div>
            <NavLink to="/tasks" className={s.sectionAction}>ЗАДАЧИ</NavLink>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={s.sectionTitleBtn}
              onClick={() => setTasksOpen((v) => !v)}
              aria-expanded={isTasksOpen}
              aria-controls="task-cards"
              title={isTasksOpen ? 'Свернуть' : 'Развернуть'}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
            >
              {`Задачи (${totalDisplayedTaskCards})`}
            </button>

            <div
              className={s.sectionControls}
              onClick={(e) => {
                const el = e.target;
                if (el.closest && el.closest('a')) return;
                setTasksOpen((v) => !v);
              }}
            >
              <ToggleLine open={isTasksOpen} ariaControls="task-cards" />
              <NavLink to="/tasks" className={s.sectionAction}>ЗАДАЧИ</NavLink>
            </div>

            <div id="task-cards" style={collapseStyle(isTasksOpen)}>
              <div style={{ minHeight: 0 }}>
                <ul className={s.cardList}>
                  {filteredDisplayedTaskCards.map((task) => {
                    const rawTaskName = task.title || `Задача #${task.id}`;
                    const taskTitle = truncateText(rawTaskName, 42);
                    const deadlineText = formatTaskDeadline(task.deadline_at);
                    const statusLabel = TASK_STATUS_LABELS[task.status] || task.status || '—';
                    const statusCls = s[`taskStatus_${task.status}`] || '';
                    const unreadComments = Math.max(0, Number(task.unread_comments_count) || 0);

                    return (
                      <li
                        key={`task-card-${task.id}`}
                        className={s.cardItem}
                        onClick={() => openTask(task)}
                        role="button"
                        aria-label={`Открыть задачу ${rawTaskName}. Дедлайн: ${deadlineText}. Статус: ${statusLabel}`}
                        title={rawTaskName}
                      >
                        <div className={`${s.newsRow} ${s.taskRow}`}>
                          <div className={`${s.newsMain} ${s.taskMain}`}>
                            <TasksIcon className={`${s.newsIcon} ${s.taskIcon}`} aria-hidden="true" />
                            <div className={s.newsText}>
                              <div className={s.newsRubric}>{deadlineText}</div>
                              <div className={`${s.newsTitle} ${s.taskTitleRow}`}>
                                <span className={s.taskTitleText}>{taskTitle}</span>
                                {unreadComments > 0 && (
                                  <span
                                    className={s.taskCommentBadge}
                                    aria-label={`Новых комментариев: ${unreadComments}`}
                                  >
                                    {unreadComments > 99 ? '99+' : unreadComments}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className={s.newsMeta}>
                            <span className={`${s.taskStatus} ${statusCls}`}>{statusLabel}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </>
        )
      ) : totalTaskNotifications === 0 ? (
        <div className={s.sectionRow}>
          <div>
            <div className={s.sectionTitlePlain}>Задачи</div>
            <div className={s.sectionEmptyText}>Непрочитанных задач нет</div>
          </div>
          <NavLink to="/tasks" className={s.sectionAction}>ЗАДАЧИ</NavLink>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={s.sectionTitleBtn}
            onClick={() => setTasksOpen((v) => !v)}
            aria-expanded={isTasksOpen}
            aria-controls="task-notifications"
            title={isTasksOpen ? 'Свернуть' : 'Развернуть'}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
          >
            {`Задачи (${totalTaskNotifications})`}
          </button>

          <div
            className={s.sectionControls}
            onClick={(e) => {
              const el = e.target;
              if (el.closest && el.closest('a')) return;
              setTasksOpen((v) => !v);
            }}
          >
            <ToggleLine open={isTasksOpen} ariaControls="task-notifications" />
            <NavLink to="/tasks" className={s.sectionAction}>ЗАДАЧИ</NavLink>
          </div>

          <div id="task-notifications" style={collapseStyle(isTasksOpen)}>
            <div style={{ minHeight: 0 }}>
              {filteredTaskNotifications.length === 0 ? (
                <div className={s.sectionEmptyText}>Нет уведомлений по выбранным фильтрам</div>
              ) : (
                <ul className={s.cardList}>
                  {filteredTaskNotifications.map((group) => {
                    const rawTaskName = group.task_title || `Задача #${group.task_id}`;
                    const taskTitle = truncateText(rawTaskName, 42);
                    const eventText = formatTaskNotificationEvent(group);
                    const eventPreview = eventText.length > 100 ? `${eventText.slice(0, 100)}…` : eventText;
                    const time = formatTime(group.created_at);
                    const extraUnread = Math.max(0, (Number(group.unread_count) || 1) - 1);
                    return (
                      <li
                        key={`task-${group.task_id}`}
                        className={s.cardItem}
                        onClick={() => openTaskNotification(group)}
                        role="button"
                        aria-label={`${eventText}. Задача: ${rawTaskName}${extraUnread > 0 ? `. Ещё событий: ${extraUnread}` : ''}`}
                        title={rawTaskName}
                      >
                        <div className={s.head}>
                          <div className={s.msgMain}>
                            <div className={s.titleRow}>
                              <TasksIcon className={`${s.itemIcon} ${s.taskIcon}`} aria-hidden="true" />
                              <div className={s.msgText}>
                                <strong className={s.itemTitle}>{taskTitle}</strong>
                                <div className={s.preview}>{eventPreview}</div>
                              </div>
                            </div>
                          </div>
                          <div className={s.msgMeta}>
                            <span className={s.time}>{time}</span>
                            {extraUnread > 0 && (
                              <span
                                className={s.badge}
                                aria-label={`ещё событий: ${extraUnread}`}
                                title={`${extraUnread} доп. уведомлений по задаче`}
                              >
                                +{extraUnread}
                              </span>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      {/* Непрочитанные новости */}
      {!hideFeedSections && (totalUnreadNews === 0 ? (
        // вариант "как у чатов/проектов": слева заголовок + подпись, справа кнопка ЛЕНТА
        <div className={s.sectionRow}>
          <div>
            <div className={s.sectionTitlePlain}>Новости</div>
            <div className={s.sectionEmptyText}>Непрочитанных новостей нет</div>
          </div>
          <NavLink to="/news" className={s.sectionAction}>ЛЕНТА</NavLink>
        </div>
      ) : (
        <>
          <button
            type="button"
            className={s.sectionTitleBtn}
            onClick={() => setNewsOpen((v) => !v)}
            aria-expanded={isNewsOpen}
            aria-controls="unread-news"
            title={isNewsOpen ? 'Свернуть' : 'Развернуть'}
            onMouseDown={(e) => e.preventDefault()}
            onTouchStart={(e) => e.preventDefault()}
          >
            {`Новости${totalUnreadNews > 0 ? ` (${totalUnreadNews})` : ''}`}
          </button>

          <div
            className={s.sectionControls}
            onClick={(e) => {
              const el = e.target;
              if (el.closest && el.closest('a')) return;
              setNewsOpen((v) => !v);
            }}
          >
            {totalUnreadNews > 0 && (<ToggleLine open={isNewsOpen} ariaControls="unread-news" />)}
            <NavLink to="/news" className={s.sectionAction}>ЛЕНТА</NavLink>
          </div>

          <div id="unread-news" style={collapseStyle(isNewsOpen)}>
            <div style={{ minHeight: 0 }}>
              {filteredNews.length === 0 ? (
                // новости есть, но их скрыли фильтром/поиском
                <div className={s.sectionEmptyText}>Нет новостей по выбранным фильтрам</div>
              ) : (
                <ul className={s.cardList}>
                  {filteredNews.map((n) => {
                    const rubric = n.rubric_title || n.rubric || n.category || '';
                    const hasRubric = Boolean(rubric);
                    const fullTitle = n.title || '';
                    const titleShort = truncateText(fullTitle, 42);
                    const time = formatTime(n.created_at);

                    const iconSrc = n.is_birthday ? birthdayNewsIcon : (n.is_top ? importantNewsIcon : newNewsIcon);
                    const iconAlt = n.is_birthday ? 'Поздравление с днём рождения' : (n.is_top ? 'Важная новость' : 'Новость');

                    return (
                      <li
                        key={n.id}
                        className={s.cardItem}
                        onClick={() => openNews(n)}
                        role="button"
                        aria-label={`Открыть новость ${fullTitle}`}
                        title={fullTitle}
                      >
                        <div className={s.newsRow}>
                          {/* слева иконка + текст */}
                          <div className={s.newsMain}>
                            <img className={s.newsIcon} src={iconSrc} alt={iconAlt} />
                            <div className={`${s.newsText} ${!hasRubric ? s.newsTextNoRubric : ''}`}>
                              {hasRubric && (<div className={s.newsRubric}>{rubric}</div>)}
                              <div className={s.newsTitle}>{titleShort}</div>
                            </div>
                          </div>

                          {/* справа время */}
                          <div className={s.newsMeta}>
                            <span className={s.newsTime}>{time}</span>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </>
      ))}



      {/* Модалка голосования */}
      {pollModal.open && pollModal.poll && (
        <PollVoteModal
          poll={pollModal.poll}
          currentEmployeeId={employeeId}
          onClose={() => setPollModal({ open: false, poll: null })}
          onSubmit={async (payload) => {
            try {
              const res = await apiFetch(`${API_URL}/polls/${pollModal.poll.id}/ballots`, {
                method: 'POST',
                body: JSON.stringify(payload),
              });

              if (!res.ok) {
                if (res.status === 409) {
                  alert('Вы уже голосовали в этом опросе. Обратитесь к администратору, если нужно изменить голос.');
                } else if (res.status === 422) {
                  alert('Нельзя голосовать за себя или выбран неверный вариант.');
                } else {
                  alert('Не удалось отправить голос');
                }
                return;
              }
              // успех: убираем виджет и закрываем модалку
              setActivePolls(prev => prev.filter(p => p.id !== pollModal.poll.id));
              setPollModal({ open: false, poll: null });
            } catch {
              alert('Не удалось отправить голос');
            }
          }}
        />
      )}
    </div>
  );
}
